/**
 * Google Analytics, loaded on every visit.
 *
 * No consent gate: the operator's decision, and the Privacy Policy states it
 * plainly rather than describing a banner that does not exist. Worth knowing
 * what that trades away — under GDPR and the UK equivalent, analytics cookies
 * are not "strictly necessary" and are normally supposed to be asked about
 * before they are set. The exposure is a regulator complaint from an EU or UK
 * visitor, not anything that breaks the product.
 *
 * The measurement id still comes from the build environment. Hardcoding it
 * would send every local run and every CI build into the production property,
 * which is how an analytics account stops being worth reading.
 */
const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Configured at build time, and therefore possible at all. */
export const analyticsAvailable = (): boolean => Boolean(MEASUREMENT_ID);

let loaded = false;

/** Injects gtag.js. Idempotent: a second call is a no-op, not a second script. */
export function loadAnalytics(): void {
  if (loaded || !MEASUREMENT_ID) return;
  loaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, {
    // The SPA sends its own page views on navigation; the automatic one would
    // only ever report the entry URL.
    send_page_view: false,
  });
}

/** Loads analytics on boot. A no-op when no measurement id was built in. */
export function initAnalytics(): void {
  loadAnalytics();
}

/**
 * One page view, with the query string removed.
 *
 * `/build?project=<uuid>` would otherwise put a customer's project id into a
 * third party's records for no analytical gain — the interesting fact is that
 * the builder was opened, not which site it opened.
 */
export function trackPageView(path: string): void {
  if (!window.gtag || !MEASUREMENT_ID) return;
  const clean = path.split('?')[0];
  window.gtag('event', 'page_view', { page_path: clean, page_location: origin() + clean });
}

const origin = (): string => (typeof location === 'undefined' ? '' : location.origin);
